import React, { useEffect, useRef } from 'react';
import { useModal } from '../../context/ModalContext';
import './FlightPerks.css';

const steps = [
  {
    num: '01',
    icon: '✈️',
    title: 'Free Flight Tickets',
    desc: 'We cover your complete round-trip airfare — no hidden charges, no booking stress.',
    tag: 'Worth ₹60,000+',
    tagClass: 'fp-tag-blue',
    accent: '#1a56db',
    accentBg: '#eff6ff',
    detail: 'Economy to business class routes covered across Russia, Georgia, Kazakhstan, Philippines & more.',
  },
  {
    num: '02',
    icon: '🚐',
    title: 'Free Airport Pickup',
    desc: 'Our dedicated ground team greets you on arrival — no waiting, no confusion in a new country.',
    tag: 'Day 1 Support',
    tagClass: 'fp-tag-green',
    accent: '#15803d',
    accentBg: '#f0fdf4',
    detail: 'Verified drivers, name-boards, local SIM assistance, and direct briefing by our country coordinator.',
  },
  {
    num: '03',
    icon: '🏠',
    title: 'Free Hostel Drop',
    desc: 'Straight from the airport to your verified hostel — arranged, confirmed, completely free.',
    tag: 'Fully Included',
    tagClass: 'fp-tag-purple',
    accent: '#7c3aed',
    accentBg: '#faf5ff',
    detail: 'Luggage assistance, hostel check-in support, and orientation guide provided on Day 1.',
  },
];

const FlightPerks = () => {
  const { openModal } = useModal();
  const sectionRef = useRef(null);

  /* Tilt effect on cards */
  useEffect(() => {
    const cards = document.querySelectorAll('.fp-tcard');
    const handleMove = (e, card) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const rotY = ((x - cx) / cx) * 10;
      const rotX = -((y - cy) / cy) * 8;
      card.style.transform = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(-6px)`;
    };
    const handleLeave = (card) => {
      card.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) translateY(0)';
    };
    cards.forEach(card => {
      card.addEventListener('mousemove', (e) => handleMove(e, card));
      card.addEventListener('mouseleave', () => handleLeave(card));
    });
    return () => {
      cards.forEach(card => {
        card.replaceWith(card.cloneNode(true));
      });
    };
  }, []);

  return (
    <section className="fp-section" id="flight-perks" ref={sectionRef}>
      {/* Background mesh blobs */}
      <div className="fp-blob fp-blob-1" aria-hidden="true" />
      <div className="fp-blob fp-blob-2" aria-hidden="true" />
      {/* Subtle dot grid */}
      <div className="fp-dotgrid" aria-hidden="true" />

      <div className="fp-inner">

        {/* ── Header ── */}
        <div className="fp-header reveal">
          <div className="sec-label">Exclusive Student Benefits</div>
          <h2 className="sec-title">
            Your Journey, <span className="fp-hl">Fully Covered</span>
          </h2>
          <p className="fp-sub">
            From India to your college city abroad — flights, pickup and hostel drop, all arranged
            and paid for by us. Zero cost to you.
          </p>
        </div>

        {/* ── 3D Timeline ── */}
        <div className="fp-timeline-wrap">

          {/* Connector track */}
          <div className="fp-track" aria-hidden="true">
            <div className="fp-track-line" />
            <div className="fp-track-plane">✈️</div>
          </div>

          {/* Steps */}
          <div className="fp-steps">
            {steps.map((s, i) => (
              <div className="fp-step reveal" key={i} style={{ transitionDelay: `${i * 0.14}s` }}>

                {/* Step number bubble */}
                <div className="fp-step-bubble" style={{ '--accent': s.accent }}>
                  <span>{s.num}</span>
                  <div className="fp-bubble-ring" />
                </div>

                {/* 3-D Card */}
                <div
                  className="fp-tcard"
                  style={{
                    '--accent': s.accent,
                    '--accent-bg': s.accentBg,
                    transitionProperty: 'transform, box-shadow',
                    transitionDuration: '0.35s',
                    transitionTimingFunction: 'ease',
                  }}
                >
                  {/* Shine overlay */}
                  <div className="fp-tcard-shine" aria-hidden="true" />

                  {/* Card top row */}
                  <div className="fp-tcard-top">
                    <div className="fp-tcard-icon" style={{ background: s.accentBg }}>
                      {s.icon}
                    </div>
                    <span className={`fp-tcard-badge ${s.tagClass}`}>{s.tag}</span>
                  </div>

                  {/* Content */}
                  <h3 className="fp-tcard-title">{s.title}</h3>
                  <p className="fp-tcard-desc">{s.desc}</p>

                  {/* Detail row */}
                  <div className="fp-tcard-detail">
                    <span className="fp-detail-dot" style={{ background: s.accent }} />
                    <span>{s.detail}</span>
                  </div>

                  {/* Bottom accent bar */}
                  <div className="fp-tcard-bar" />
                </div>

              </div>
            ))}
          </div>
        </div>

        {/* ── Value Stats ── */}
        <div className="fp-stats reveal">
          {[
            { v: '₹60K+', l: 'Flight Value' },
            { v: '100%', l: 'Free of Cost' },
            { v: 'Day 1', l: 'Support Starts' },
            { v: '24/7', l: 'Ground Team' },
          ].map((s, i) => (
            <React.Fragment key={i}>
              <div className="fp-stat">
                <span className="fp-stat-val">{s.v}</span>
                <span className="fp-stat-lbl">{s.l}</span>
              </div>
              {i < 3 && <div className="fp-stat-sep" />}
            </React.Fragment>
          ))}
        </div>

        {/* ── CTA Banner ── */}
        <div className="fp-cta reveal">
          {/* Decorative glow */}
          <div className="fp-cta-glow" aria-hidden="true" />

          <div className="fp-cta-left">
            <div className="fp-cta-plane">✈️</div>
            <div>
              <strong className="fp-cta-title">Ready to fly?</strong>
              <p className="fp-cta-desc">
                Enroll today — we handle flights, pickup &amp; your entire first day abroad.
              </p>
            </div>
          </div>

          {/* ← Uses openModal exactly like Hero */}
          <button onClick={openModal} className="fp-cta-btn">
            Claim Your Free Flight
            <svg className="fp-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

      </div>
    </section>
  );
};

export default FlightPerks;